import React from 'react';
import { Alert, TextInput, Textarea } from '@parsina/framework';
import { useClinicalTranslation } from '@parsina/framework/clinical';
import { C, soft } from '@parsina/framework/tokens';

import { StepHeading, PrimaryAction } from '../../components/auth/OnboardingParts.jsx';
import { completeCouncilStep, councilStepError, useCouncilLookup } from './councilLookup.js';

/**
 * «مشخصات پزشک» — the doctor's professional identity, inside the clinic wizard.
 *
 * A port of the doctor panel's step 1 (doctor-app src/screens/Onboarding,
 * `StepDoctorInfo`). It is a PORT and not a second design: a doctor who signs
 * up at the clinic panel and one who signs up at the doctor panel are the same
 * person filling in the same council number, and two dialects of that screen is
 * two places to fix a registry bug. Change it there first.
 *
 * ── It talks to the DOCTOR BFF, not this panel's ───────────────────────────
 *
 * `lookupCouncilMember` and `completeOnboardingStep1` live in the doctor BFF,
 * which owns the practitioner record. `bffQuery`/`bffMutation` here are the
 * CLINICAL transport (lib/clinicalHost.js), already pointed at that BFF with
 * this panel's token — the same channel the visit room uses. The clinic BFF
 * exposes none of these operations and must not grow copies of them.
 *
 * Because the transport is the clinical one, the words are the clinical
 * catalogue's too (`auth.onboarding.*`), which is why this file reads
 * `useClinicalTranslation` rather than the panel's own `useTranslation`. The
 * caller mounts it inside `<ClinicalI18n>`.
 *
 * ── One field, and the rest is READ ────────────────────────────────────────
 *
 * The name on a prescription is the one سازمان نظام پزشکی holds against the
 * council number, so the wizard reads it instead of asking anyone. The card
 * below the field is a courtesy: the BFF resolves the code again and writes the
 * council's values whatever this screen sends.
 */

/** One line of the council's record. Rendered, never edited. */
const RegistryRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: '0.8125rem', lineHeight: 1.9 }}>
    <span style={{ color: C.textSecondary, minWidth: 96, flexShrink: 0 }}>{label}</span>
    <span style={{ color: C.textPrimary, fontWeight: 600 }}>{value}</span>
  </div>
);

export default function DoctorIdentityStep({ onDone, onSkip, disabled = false }) {
  const { t } = useClinicalTranslation();

  // The lookup — when it runs, latest-wins, and the registry's own failures
  // kept apart from a wrong number — is shared with «خوش آمدید!» on «شروع».
  const {
    code, member, looking, error, setError, onChange: setCodeField, onBlur: handleBlur,
  } = useCouncilLookup(t);
  const [bio, setBio] = React.useState('');
  const [saving, setSaving] = React.useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!member || saving || disabled) return;
    setSaving(true);
    setError(null);
    try {
      const doctor = await completeCouncilStep(member, { bio });
      if (!doctor?.id) throw new Error(t('errors.save.doctorProfile'));
      // Awaited: the caller asks the BFF one more question (is a wheel owed?)
      // before it decides which phase comes next, and an un-awaited call would
      // drop the button back to «مرحله بعد» over a form already submitted.
      await onDone({ doctor });
    } catch (err) {
      setError(councilStepError(err, t));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="onboarding-doctor-step">
      <StepHeading
        title={t('auth.onboarding.professional')}
        subtitle={t('auth.onboarding.registryPrompt')}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <TextInput
            label={t('common.labels.medicalCode')}
            required
            value={code}
            onChange={(e) => setCodeField(e.target.value)}
            onBlur={handleBlur}
            dir="ltr"
            disabled={saving}
            data-testid="onboarding-medical-code"
          />
          {/* «در حال استعلام...» is never the button's label: the field stays
              enabled while it shows, so the doctor can keep typing and the
              newer number supersedes the one being waited for. */}
          <div
            data-testid="onboarding-lookup-status"
            aria-live="polite"
            style={{ fontSize: '0.71875rem', color: C.textSecondary, marginTop: 6, minHeight: 16, lineHeight: 1.6 }}
          >
            {looking ? t('auth.onboarding.lookingUp') : ''}
          </div>
        </div>

        {member ? (
          <div
            data-testid="onboarding-council-record"
            style={{
              border: `1px solid ${C.border}`, borderRadius: 10,
              padding: '12px 14px', background: soft(C.teal, 0.06),
              display: 'flex', flexDirection: 'column', gap: 2,
            }}
          >
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: C.teal, marginBottom: 6 }}>
              {t('auth.onboarding.registryTitle')}
            </div>
            <RegistryRow label={t('common.labels.fullName')} value={member.fullName} />
            <RegistryRow label={t('common.labels.medicalCode')} value={member.councilNumber} />
            {member.degree ? <RegistryRow label={t('auth.onboarding.registryDegree')} value={member.degree} /> : null}
            {member.specialty ? <RegistryRow label={t('common.labels.specialty')} value={member.specialty} /> : null}
            {member.memberType ? <RegistryRow label={t('auth.onboarding.registryMemberType')} value={member.memberType} /> : null}
            {member.province || member.city ? (
              <RegistryRow
                label={t('auth.onboarding.registryCity')}
                value={[member.province, member.city].filter(Boolean).join(' — ')}
              />
            ) : null}
            <div style={{ fontSize: '0.6875rem', color: C.textSecondary, marginTop: 8, lineHeight: 1.8 }}>
              {t('auth.onboarding.registryLocked')}
            </div>
          </div>
        ) : null}

        <Textarea
          label={t('auth.onboarding.bioOptional')}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={3}
          disabled={saving}
          data-testid="onboarding-bio"
        />

        {error ? <Alert variant="error" data-testid="onboarding-doctor-error">{error}</Alert> : null}

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          {/* Leaving the doctor track is always available, and it is not a
              cancel: the clinic still gets registered, just without a
              practitioner record. Someone who mistook the button on «شروع» for
              «I work at a clinic» must not be trapped behind a council number
              they do not have. */}
          <PrimaryAction secondary type="button" onClick={onSkip} disabled={saving} data-testid="onboarding-doctor-skip">
            {t('common.actions.back')}
          </PrimaryAction>
          <PrimaryAction
            fullWidth
            type="submit"
            disabled={saving || disabled || !member}
            data-testid="onboarding-doctor-next"
          >
            {saving ? t('auth.onboarding.saving') : t('auth.onboarding.next')}
          </PrimaryAction>
        </div>
      </div>
    </form>
  );
}
